import {
    useLocation,
    useParams,
    useNavigate,
  } from "react-router-dom";
  
  import {
    useEffect,
    useState,
    useMemo,
  } from "react";
  
  import {
    doc,
    getDoc,
  } from "firebase/firestore";
  
  import { db } from "../firebase";
  import "./OtherBetScore.css";
  
  const TIMEZONE = "Africa/Dar_es_Salaam";
  
  const QUANTITY_SETTINGS = {
    MAX_QUANTITY: 1900000000,
    START_PERCENT: 55,
    DYNAMIC_WINDOW_MINUTES: 24 * 60,
    MIN_START_QUANTITY: 10000000,
    QUANTITY_STEP: 1000,
  };
  
  const COLLECTIONS = [
    "matches",
    "basketballmatches",
    "tennismatches",
    "tabletennis",
    "icehockeymatches",
    "cricketmatches",
  ];
  
  /* =======================================================
     DETECT SPORT
  ======================================================= */
  
  function getSport(match) {
    const value = String(
      match?.sport || match?._collection || ""
    ).toLowerCase();
  
    if (
      value.includes("icehockey") ||
      value.includes("ice-hockey") ||
      value.includes("hockey")
    ) {
      return "icehockey";
    }
  
    if (value.includes("basketball")) {
      return "basketball";
    }
  
    if (
      value.includes("tabletennis") ||
      value.includes("table-tennis") ||
      value.includes("table tennis")
    ) {
      return "tabletennis";
    }
  
    if (value.includes("tennis")) {
      return "tennis";
    }
  
    if (value.includes("cricket")) {
      return "cricket";
    }
  
    if (match?._collection === "icehockeymatches") {
      return "icehockey";
    }
  
    if (match?._collection === "basketballmatches") {
      return "basketball";
    }
  
    if (match?._collection === "tennismatches") {
      return "tennis";
    }
  
    if (match?._collection === "tabletennis") {
      return "tabletennis";
    }
  
    if (match?._collection === "cricketmatches") {
      return "cricket";
    }
  
    return "football";
  }
  
  /* =======================================================
     NORMALISE CORRECT SCORES
     Supports arrays and objects:
     correctScores, correctScoreOdds, scoreOdds
  ======================================================= */
  
  function normaliseCorrectScores(match) {
    if (!match) return [];
  
    const possibleFields = [
      match.correctScores,
      match.correctScoreOdds,
      match.scoreOdds,
    ];
  
    let sourceScores = [];
  
    for (const field of possibleFields) {
      if (Array.isArray(field) && field.length > 0) {
        sourceScores = field;
        break;
      }
  
      if (
        field &&
        typeof field === "object" &&
        !Array.isArray(field)
      ) {
        sourceScores = Object.entries(field).map(
          ([score, value]) => ({
            score,
            odds:
              value && typeof value === "object"
                ? value.odds
                : value,
            quantity:
              value && typeof value === "object"
                ? value.quantity ?? null
                : null,
          })
        );
  
        if (sourceScores.length > 0) {
          break;
        }
      }
    }
  
    return sourceScores
      .filter((item) => {
        if (!item || item.score == null) {
          return false;
        }
  
        const score = String(item.score).trim();
        const odds = Number(item.odds);
  
        return (
          score !== "" &&
          Number.isFinite(odds) &&
          odds > 1
        );
      })
      .map((item, index) => ({
        id: item.id || `${String(item.score).trim()}-${index}`,
        score: String(item.score)
          .trim()
          .replace(/\s+/g, "")
          .replace(":", "-"),
        odds: Number(item.odds),
        quantity: item.quantity ?? null,
      }));
  }
  
  /* =======================================================
     BET SCORES COMPONENT
  ======================================================= */
  
  export default function BetScores() {
    const { state } = useLocation();
    const { id } = useParams();
    const navigate = useNavigate();
  
    const initialMatch = state?.match || state || null;
  
    const [match, setMatch] = useState(initialMatch);
    const [scores, setScores] = useState([]);
    const [selected, setSelected] = useState(null);
    const [tab, setTab] = useState("ant");
  
    const [matchLoading, setMatchLoading] = useState(
      !initialMatch
    );
  
    const [scoresLoading, setScoresLoading] = useState(true);
  
    const [currentTime, setCurrentTime] = useState(
      () => new Date()
    );
  
    /* =====================================================
       LIVE CLOCK
    ===================================================== */
  
    useEffect(() => {
      const interval = setInterval(() => {
        setCurrentTime(new Date());
      }, 60000);
  
      return () => clearInterval(interval);
    }, []);
  
    /* =====================================================
       LOAD MATCH FROM FIREBASE
    ===================================================== */
  
    useEffect(() => {
      if (match) {
        setMatchLoading(false);
        return;
      }
  
      if (!id) {
        setMatchLoading(false);
        return;
      }
  
      let cancelled = false;
  
      async function fetchMatch() {
        setMatchLoading(true);
  
        try {
          let foundMatch = null;
  
          for (const collectionName of COLLECTIONS) {
            const snapshot = await getDoc(
              doc(db, collectionName, id)
            );
  
            if (cancelled) return;
  
            if (snapshot.exists()) {
              foundMatch = {
                id: snapshot.id,
                ...snapshot.data(),
                _collection: collectionName,
              };
  
              break;
            }
          }
  
          if (cancelled) return;
  
          setMatch(foundMatch);
  
          if (!foundMatch) {
            console.error(
              "Mechi haijapatikana kwenye Firebase collections."
            );
          }
        } catch (error) {
          if (!cancelled) {
            console.error("Match load error:", error);
            setMatch(null);
          }
        } finally {
          if (!cancelled) {
            setMatchLoading(false);
          }
        }
      }
  
      fetchMatch();
  
      return () => {
        cancelled = true;
      };
    }, [id, match]);
  
    /* =====================================================
       READ CORRECT SCORES
    ===================================================== */
  
    useEffect(() => {
      if (!match) {
        setScores([]);
        setScoresLoading(false);
        return;
      }
  
      setScoresLoading(true);
  
      try {
        const scoreData = normaliseCorrectScores(match);
        setScores(scoreData);
      } catch (error) {
        console.error("Scores parse error:", error);
        setScores([]);
      } finally {
        setScoresLoading(false);
      }
    }, [match]);
  
    /* =====================================================
       PARSE MATCH DATE AND TIME
    ===================================================== */
  
    const parseMatchDate = (date, time) => {
      if (!date) return null;
  
      if (typeof date?.toDate === "function") {
        return date.toDate();
      }
  
      if (date instanceof Date) {
        return date;
      }
  
      const dateString = String(date).trim();
      const timeString = time
        ? String(time).trim()
        : "00:00";
  
      if (
        dateString.includes("T") ||
        dateString.includes(" ")
      ) {
        const normalized = dateString.replace(" ", "T");
  
        const hasTimezone =
          /Z$|[+-]\d{2}:\d{2}$/.test(normalized);
  
        const dateTimeString = hasTimezone
          ? normalized
          : `${normalized}+03:00`;
  
        const parsed = new Date(dateTimeString);
  
        if (!Number.isNaN(parsed.getTime())) {
          return parsed;
        }
      }
  
      const eatDateTime = new Date(
        `${dateString}T${timeString}:00+03:00`
      );
  
      return Number.isNaN(eatDateTime.getTime())
        ? null
        : eatDateTime;
    };
  
    const matchStartTime = useMemo(() => {
      if (!match) return null;
  
      return parseMatchDate(
        match.date,
        match.time
      );
    }, [match]);
  
    const getMinutesUntilStart = () => {
      if (!matchStartTime) return null;
  
      return (
        (matchStartTime.getTime() -
          currentTime.getTime()) /
        60000
      );
    };
  
    /* =====================================================
       GET BASE QUANTITY
    ===================================================== */
  
    const getBaseQuantity = (item, index) => {
      if (
        item?.quantity !== undefined &&
        item?.quantity !== null &&
        item?.quantity !== ""
      ) {
        const value = Number(item.quantity);
  
        if (Number.isFinite(value) && value > 0) {
          return Math.min(
            value,
            QUANTITY_SETTINGS.MAX_QUANTITY
          );
        }
      }
  
      const fallbackQuantities = [
        13256000,
        27381000,
        47381000,
        68243000,
        91756000,
        126547000,
        174263000,
        238451000,
        326784000,
        417526000,
        538214000,
        647381000,
        782645000,
        894217000,
        1026380000,
        1184720000,
        1325680000,
        1473820000,
        1584263000,
        1718526000,
        1826374000,
        1897426000,
      ];
  
      return Math.min(
        fallbackQuantities[
          index % fallbackQuantities.length
        ],
        QUANTITY_SETTINGS.MAX_QUANTITY
      );
    };
  
    /* =====================================================
       STARTING QUANTITY
    ===================================================== */
  
    const getStartingQuantity = (
      baseQuantity,
      index
    ) => {
      const variations = [
        0.73,
        0.81,
        0.87,
        0.92,
        0.76,
        0.84,
        0.89,
        0.95,
        0.79,
        0.86,
        0.91,
        0.97,
      ];
  
      let starting =
        baseQuantity *
        (QUANTITY_SETTINGS.START_PERCENT / 100);
  
      starting *= variations[
        index % variations.length
      ];
  
      starting = Math.max(
        QUANTITY_SETTINGS.MIN_START_QUANTITY,
        starting
      );
  
      return Math.min(
        starting,
        baseQuantity,
        QUANTITY_SETTINGS.MAX_QUANTITY
      );
    };
  
    /* =====================================================
       ROUND QUANTITY
    ===================================================== */
  
    const roundQuantity = (value) => {
      const numericValue = Number(value);
  
      if (!Number.isFinite(numericValue)) {
        return 0;
      }
  
      const step = QUANTITY_SETTINGS.QUANTITY_STEP;
  
      return Math.round(numericValue / step) * step;
    };
  
    /* =====================================================
       ADD NATURAL DIGITS
    ===================================================== */
  
    const addNaturalDigits = (value, index) => {
      const patterns = [
        132,
        347,
        581,
        726,
        843,
        263,
        419,
        657,
        782,
        915,
        536,
        684,
      ];
  
      return (
        Number(value) +
        patterns[index % patterns.length] * 1000
      );
    };
  
    /* =====================================================
       DYNAMIC QUANTITY
    ===================================================== */
  
    const calculateDynamicQuantity = (
      baseQuantity,
      index
    ) => {
      const safeBase = Math.min(
        Number(baseQuantity),
        QUANTITY_SETTINGS.MAX_QUANTITY
      );
  
      if (
        !Number.isFinite(safeBase) ||
        safeBase <= 0
      ) {
        return 0;
      }
  
      const minutesUntilStart = getMinutesUntilStart();
  
      if (minutesUntilStart === null) {
        return roundQuantity(
          Math.min(
            addNaturalDigits(safeBase, index),
            safeBase,
            QUANTITY_SETTINGS.MAX_QUANTITY
          )
        );
      }
  
      if (minutesUntilStart <= 0) {
        return roundQuantity(safeBase);
      }
  
      const startingQuantity = getStartingQuantity(
        safeBase,
        index
      );
  
      const dynamicWindow =
        QUANTITY_SETTINGS.DYNAMIC_WINDOW_MINUTES;
  
      if (minutesUntilStart >= dynamicWindow) {
        return roundQuantity(
          Math.min(
            addNaturalDigits(
              startingQuantity,
              index
            ),
            safeBase,
            QUANTITY_SETTINGS.MAX_QUANTITY
          )
        );
      }
  
      const progress = Math.max(
        0,
        Math.min(
          1,
          1 - minutesUntilStart / dynamicWindow
        )
      );
  
      const rawQuantity =
        startingQuantity +
        (safeBase - startingQuantity) * progress;
  
      return roundQuantity(
        Math.min(
          addNaturalDigits(rawQuantity, index),
          safeBase,
          QUANTITY_SETTINGS.MAX_QUANTITY
        )
      );
    };
  
    const getQuantity = (item, index) => {
      return calculateDynamicQuantity(
        getBaseQuantity(item, index),
        index
      );
    };
  
    /* =====================================================
       FORMAT QUANTITY
    ===================================================== */
  
    const formatQuantity = (quantity) => {
      const value = Number(quantity);
  
      if (!Number.isFinite(value)) {
        return "0.00";
      }
  
      return value.toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };
  
    /* =====================================================
       DISPLAY ODDS
       Odds hazizidishwi kwa 10.
    ===================================================== */
  
    const getDisplayedOdds = (odds) => {
      if (
        odds === undefined ||
        odds === null ||
        odds === ""
      ) {
        return 0;
      }
  
      const numericValue = Number(
        String(odds).replace("%", "").trim()
      );
  
      if (!Number.isFinite(numericValue)) {
        return 0;
      }
  
      return Number.isInteger(numericValue)
        ? numericValue
        : Number(numericValue.toFixed(2));
    };
  
    /* =====================================================
       FORMAT ODDS
    ===================================================== */
  
    const formatOdds = (
      odds,
      multiplier = 1
    ) => {
      if (
        odds === undefined ||
        odds === null ||
        odds === ""
      ) {
        return "-";
      }
  
      const numericValue = Number(
        String(odds).replace("%", "").trim()
      );
  
      if (!Number.isFinite(numericValue)) {
        return "-";
      }
  
      const convertedValue =
        numericValue * multiplier;
  
      const finalValue = Number.isInteger(
        convertedValue
      )
        ? convertedValue
        : Number(convertedValue.toFixed(2));
  
      return `${finalValue}%`;
    };
  
    /* =====================================================
       OPEN MARKET CHART
    ===================================================== */
  
    const openMarketChart = () => {
      const matchId = match?.id || id;
  
      if (!matchId) {
        console.error("Match ID haipo");
        return;
      }
  
      navigate(`/market-chart/${matchId}`, {
        state: { match },
      });
    };
  
    /* =====================================================
       HANDLE BET
    ===================================================== */
  
    const handleClick = (item, index) => {
      if (!item) return;
  
      const quantity = getQuantity(item, index);
  
      const displayedOdds = getDisplayedOdds(
        item.odds
      );
  
      const selectedItem = {
        ...item,
        odds: displayedOdds,
        quantity,
        mode: tab,
      };
  
      setSelected(selectedItem);
  
      const selection = {
        score: item.score,
        odds: displayedOdds,
        quantity,
        mode: tab,
      };
  
      setTimeout(() => {
        navigate("/other-betplace", {
          state: {
            match,
            selection,
          },
        });
      }, 200);
    };
  
    /* =====================================================
       CHANGE TAB
    ===================================================== */
  
    const changeTab = (newTab) => {
      setTab(newTab);
      setSelected(null);
    };
  
    /* =====================================================
       MATCH LOADING
    ===================================================== */
  
    if (matchLoading) {
      return (
        <div className="bet-page">
          <div className="loading">
            Loading match...
          </div>
        </div>
      );
    }
  
    /* =====================================================
       MATCH NOT FOUND
    ===================================================== */
  
    if (!match) {
      return (
        <div className="bet-page">
          <div className="loading">
            Match not found
          </div>
        </div>
      );
    }
  
    /* =====================================================
       TEAM NAMES
    ===================================================== */
  
    const teamA =
      match.A ||
      match.teamA ||
      match.homeTeam ||
      match.team1 ||
      "Team A";
  
    const teamB =
      match.B ||
      match.teamB ||
      match.awayTeam ||
      match.team2 ||
      "Team B";
  
    const league = match.league || "League";
    const sport = getSport(match);
  
    const minutesUntilStart =
      getMinutesUntilStart();
  
    /* =====================================================
       MATCH STATUS
    ===================================================== */
  
    const getMatchStatusText = () => {
      if (minutesUntilStart === null) {
        return "";
      }
  
      if (minutesUntilStart <= 0) {
        return "Started";
      }
  
      if (minutesUntilStart < 60) {
        return `Starts in ${Math.ceil(
          minutesUntilStart
        )} min`;
      }
  
      if (minutesUntilStart < 24 * 60) {
        const hours = Math.floor(
          minutesUntilStart / 60
        );
  
        const minutes = Math.ceil(
          minutesUntilStart % 60
        );
  
        return `Starts in ${hours}h ${minutes}m`;
      }
  
      const days = Math.floor(
        minutesUntilStart / (24 * 60)
      );
  
      const hours = Math.floor(
        (minutesUntilStart % (24 * 60)) / 60
      );
  
      return `Starts in ${days}d ${hours}h`;
    };
  
    /* =====================================================
       SPORT ICON
    ===================================================== */
  
    const sportIcon = {
      football: "⚽",
      basketball: "🏀",
      tennis: "🎾",
      tabletennis: "🏓",
      icehockey: "🏒",
      cricket: "🏏",
    }[sport] || "🏆";
  
    /* =====================================================
       UI
    ===================================================== */
  
    return (
      <div className="bet-page">
        {/* MATCH HEADER */}
  
        <div className="bet-header">
          <div className="teams">
            <div className="team">
              <div
                className="team-ball"
                aria-label={sport}
              >
                {sportIcon}
              </div>
  
              <span>{teamA}</span>
            </div>
  
            <span className="vs">VS</span>
  
            <div className="team">
              <div
                className="team-ball"
                aria-label={sport}
              >
                {sportIcon}
              </div>
  
              <span>{teamB}</span>
            </div>
          </div>
  
          <p className="league">{league}</p>
  
          {getMatchStatusText() && (
            <div className="match-countdown">
              {getMatchStatusText()}
            </div>
          )}
        </div>
  
        {/* TABS */}
  
        <div className="tabs">
          <div
            className={
              tab === "ant"
                ? "tab active"
                : "tab"
            }
            role="button"
            tabIndex={0}
            onClick={() => changeTab("ant")}
            onKeyDown={(event) => {
              if (
                event.key === "Enter" ||
                event.key === " "
              ) {
                event.preventDefault();
                changeTab("ant");
              }
            }}
          >
            SecureScore
          </div>
  
          <div
            className={
              tab === "correct"
                ? "tab active"
                : "tab"
            }
            role="button"
            tabIndex={0}
            onClick={() => changeTab("correct")}
            onKeyDown={(event) => {
              if (
                event.key === "Enter" ||
                event.key === " "
              ) {
                event.preventDefault();
                changeTab("correct");
              }
            }}
          >
            UnsecureScore
          </div>
        </div>
  
        {/* SCORE LOADING */}
  
        {scoresLoading && (
          <div className="loading">
            Loading scores...
          </div>
        )}
  
        {/* SCORE TABLE */}
  
        {!scoresLoading && scores.length > 0 && (
          <div className="score-table">
            <div className="score-table-header">
              <div>Item</div>
  
              <div>Profit</div>
  
              <div className="quantity-title">
                Quantity
              </div>
  
              <div className="chart-header">
                <button
                  type="button"
                  className="header-chart-btn"
                  onClick={openMarketChart}
                  title="Market Chart"
                  aria-label="Open Market Chart"
                >
                  📈 🔺
                </button>
              </div>
            </div>
  
            {scores.map((item, index) => {
              const quantity = getQuantity(
                item,
                index
              );
  
              const displayedOdds =
                getDisplayedOdds(item.odds);
  
              const active =
                selected?.score === item.score;
  
              return (
                <div
                  key={
                    item.id ||
                    `${item.score}-${index}`
                  }
                  className={
                    active
                      ? "score-table-row selected-row"
                      : "score-table-row"
                  }
                >
                  <div className="table-score">
                    {item.score}
                  </div>
  
                  <div className="table-profit">
                    {formatOdds(displayedOdds, 1)}
                  </div>
  
                  <div className="table-quantity">
                    TZS {formatQuantity(quantity)}
                  </div>
  
                  <button
                    type="button"
                    className="table-bet-btn"
                    onClick={() =>
                      handleClick(item, index)
                    }
                    aria-label={`Bet ${item.score}`}
                  >
                    BET
                  </button>
                </div>
              );
            })}
          </div>
        )}
  
        {/* NO SCORES */}
  
        {!scoresLoading && scores.length === 0 && (
          <div className="loading">
            Mechi hii haina correct scores na odds
            zilizohifadhiwa.
          </div>
        )}
  
        {/* SELECTED BET */}
  
        {selected && (
          <div className="bet-box">
            Selected:{" "}
            <strong>{selected.score}</strong>
            {" — "}
            {formatOdds(selected.odds, 1)}
            {" — TZS "}
            {formatQuantity(selected.quantity)}
          </div>
        )}
      </div>
    );
  }